/**
 * Typed client for the developer cabinet API.
 *
 * Auth is a session cookie set by the API on the OAuth callback. The portal is
 * served from a different origin, so every request uses
 * `credentials: 'include'` and the API must allow that origin explicitly.
 */

export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? 'https://appwin-api.korova313131.workers.dev';

export interface Session {
  developerId: string;
  email: string;
  displayName: string;
  role: 'dev' | 'admin';
  expiresAt: number;
}

export interface DeveloperApp {
  id: string;
  name: string;
  category: string;
  summary: string;
  status: 'draft' | 'in_review' | 'published' | 'delisted';
  releaseCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface ReleaseView {
  id: string;
  version: string;
  kind: string;
  scope: string;
  sizeBytes: number;
  status: 'in_review' | 'published' | 'rejected' | 'yanked';
  avStatus: string;
  signatureStatus: string;
  publishedAt: number | null;
}

export interface DeveloperStats {
  apps: number;
  releasesPublished: number;
  releasesInReview: number;
  installs7d: number;
  updates7d: number;
  successRate7d: number | null;
}

export interface ApiKey {
  id: string;
  label: string | null;
  scope: 'read' | 'publish';
  createdAt: number;
  revokedAt: number | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}/v1${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  // 204 is "no content" (e.g. no client manifest); callers that expect a body
  // never hit it, so returning undefined is safe.
  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let code = 'internal';
    let message = `Ошибка запроса (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // keep the status-based message
    }
    throw new ApiError(response.status, code, message);
  }
  return (await response.json()) as T;
}

export const api = {
  /** Starts the OAuth flow in the browser. */
  signInWithGithub(): void {
    window.location.href = `${API_BASE}/v1/auth/github/start`;
  },

  session: () => request<Session>('/auth/session'),
  logout: () => request<{ signedOut: boolean }>('/auth/logout', { method: 'POST' }),

  stats: () => request<DeveloperStats>('/dev/stats'),
  apps: () => request<{ items: DeveloperApp[] }>('/dev/apps'),
  createApp: (input: CreateAppInput) =>
    request<DeveloperApp>('/dev/apps', { method: 'POST', body: JSON.stringify(input) }),
  app: (appId: string) => {
    const [developer, app] = appId.split('/');
    return request<DeveloperApp & { releases: ReleaseView[] }>(`/dev/apps/${developer}/${app}`);
  },

  submitRelease: (appId: string, input: SubmitReleaseInput) => {
    const [developer, app] = appId.split('/');
    return request<{ release: ReleaseView }>(`/dev/apps/${developer}/${app}/releases`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  apiKeys: () => request<{ items: ApiKey[] }>('/dev/api-keys'),
  createApiKey: (input: { label?: string; scope: 'read' | 'publish' }) =>
    request<{ id: string; token: string; scope: string; createdAt: number }>('/dev/api-keys', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeApiKey: (id: string) => request<{ revoked: boolean }>(`/dev/api-keys/${id}`, { method: 'DELETE' }),
};

export interface CreateAppInput {
  id: string;
  name: string;
  category: string;
  summary: Record<string, string>;
  description: Record<string, string>;
  homepageUrl?: string;
  license?: string;
}

export interface SubmitReleaseInput {
  version: string;
  platform: 'windows';
  arch: Array<'x86' | 'x64' | 'arm64' | 'universal'>;
  kind: 'msix' | 'appx' | 'msi' | 'exe' | 'zip';
  scope: 'per_user' | 'machine' | 'portable';
  /** Publisher-hosted installer; Appwin stores only the link. */
  downloadUrl: string;
  sha256: string;
  sizeBytes?: number;
  silentArgs?: string[];
  upgradeArgs?: string[];
  minOs?: string;
  changelog?: Record<string, string>;
}

